import {Component, Input} from '@angular/core';
import {MailSettings} from '@models/adminstration/organization/mail/mail-settings';
import {MAIL_TEMPLATE_TYPE} from '@constants/administration/organization/mail-template/mail-template-type';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {MailTemplateService} from '@services/administration/organization/configuration/mail-template/mail-template.service';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {MailTemplate} from '@models/adminstration/organization/mail/mail-template';
import {MailPreviewComponent} from '@components/administration/organization/configuration/mail/mail-preview/mail-preview.component';
import {ModalService} from '@services/modal/modal.service';

@Component({
    selector: 'scitis-admin-organization-mail-settings',
    templateUrl: './mail-settings.component.html',
    styleUrls: ['./mail-settings.component.scss'],
    standalone: false
})
export class MailSettingsComponent {
  private CURRENT_PAGE_NAME = 'MailSettingsComponent';
  @Input()
  isAdminRole: boolean = false;
  @Input()
  isSuperAdminRole: boolean = false;
  @Input()
  organizationId: number = -1;
  @Input()
  organizationName: string = '';
  @Input()
  settings: MailSettings = new MailSettings();

  welcomeMailCompleted: boolean = true;
  resetPasswordMailCompleted: boolean = true;
  changePasswordMailCompleted: boolean = true;
  selectedTab: string = MAIL_TEMPLATE_TYPE.WELCOME_EMAIL;
  templateType: string = MAIL_TEMPLATE_TYPE.WELCOME_EMAIL.toLowerCase();
  templateTypes = MAIL_TEMPLATE_TYPE;

  constructor(private modalService: ModalService,
              private loadingIndicator: LoadingIndicatorService,
              private notification: NotificationService,
              private translate: TranslateService,
              private mailTemplateService: MailTemplateService) {
  }

  onResetEmailTemplate() {
    this.loadingIndicator.show(this.translate.instant('Resetting email template'));
    this.mailTemplateService.getFallbackTemplate(this.organizationId, this.selectedTab).subscribe((success: MailTemplate) => {
      this.resetTemplateByType(this.selectedTab, success);
      this.loadingIndicator.hide();
      const successMessage = this.translate.instant('The organization email template reset.');
      this.notification.success(successMessage, this.CURRENT_PAGE_NAME, false);
    }, (error) => {
      this.loadingIndicator.hide();
      const errorMessage = this.translate.instant('There was an error resetting email template');
      this.notification.error(errorMessage, error, this.CURRENT_PAGE_NAME, true);
    });
  }

  private resetTemplateByType(type: string, fallbackTemplate: MailTemplate) {

    const message = fallbackTemplate.message;
    const subject = fallbackTemplate.subject;

    switch (type) {
      case MAIL_TEMPLATE_TYPE.WELCOME_EMAIL: {
        this.settings.welcomeMail.message = message;
        this.settings.welcomeMail.subject = subject;
        break;
      }

      case MAIL_TEMPLATE_TYPE.PASSWORD_RESET_EMAIL: {
        this.settings.resetPasswordMail.message = message;
        this.settings.resetPasswordMail.subject = subject;
        break;
      }

      case MAIL_TEMPLATE_TYPE.PASSWORD_CHANGE_EMAIL: {
        this.settings.changePasswordMail.message = message;
        this.settings.changePasswordMail.subject = subject;
        break;
      }
    }
  }

  onEmailPreview() {
    let message = '', subject = '';
    switch (this.selectedTab) {
      case MAIL_TEMPLATE_TYPE.WELCOME_EMAIL: {
        message = this.settings.welcomeMail.message;
        subject = this.settings.welcomeMail.subject;
        break;
      }

      case MAIL_TEMPLATE_TYPE.PASSWORD_RESET_EMAIL: {
        message = this.settings.resetPasswordMail.message;
        subject = this.settings.resetPasswordMail.subject;
        break;
      }

      case MAIL_TEMPLATE_TYPE.PASSWORD_CHANGE_EMAIL: {
        message = this.settings.changePasswordMail.message;
        subject = this.settings.changePasswordMail.subject;
        break;
      }
    }

    const modalRef = this.modalService.open(MailPreviewComponent, {size: 'lg'});
    modalRef.componentInstance.templateType = this.templateType;
    modalRef.componentInstance.subject = subject;
    modalRef.componentInstance.message = message;

  }

  onSaveMailTemplateDetails() {
    this.loadingIndicator.show(this.translate.instant('Saving email templates'));
    this.mailTemplateService.saveTemplates(this.organizationId, this.settings).subscribe((success: MailSettings) => {
      this.loadingIndicator.hide();
      this.settings = success;
      const successMessage = this.translate.instant('The organization email templates saved.');
      this.notification.success(successMessage, this.CURRENT_PAGE_NAME, false);
    }, (error) => {
      this.loadingIndicator.hide();
      const errorMessage = this.translate.instant('There was an error saving email templates');
      this.notification.error(errorMessage, error, this.CURRENT_PAGE_NAME, true);
    });
  }

  public onWelcomeEmailTemplateSelect() {
    this.selectedTab = MAIL_TEMPLATE_TYPE.WELCOME_EMAIL;
    this.templateType = MAIL_TEMPLATE_TYPE.WELCOME_EMAIL.toLowerCase();
  }

  public onPasswordResetEmailTemplateSelect() {
    this.selectedTab = MAIL_TEMPLATE_TYPE.PASSWORD_RESET_EMAIL;
    this.templateType = MAIL_TEMPLATE_TYPE.PASSWORD_RESET_EMAIL.toLowerCase();
  }

  public onPasswordChangeEmailTemplateSelect() {
    this.selectedTab = MAIL_TEMPLATE_TYPE.PASSWORD_CHANGE_EMAIL;
    this.templateType = MAIL_TEMPLATE_TYPE.PASSWORD_RESET_EMAIL.toLowerCase();
  }

  onEmailValidityChange(mailTemplateType: MAIL_TEMPLATE_TYPE, $event: boolean) {
    switch (mailTemplateType) {
      case MAIL_TEMPLATE_TYPE.WELCOME_EMAIL: {
        this.welcomeMailCompleted = $event;
        break;
      }
      case MAIL_TEMPLATE_TYPE.PASSWORD_RESET_EMAIL: {
        this.resetPasswordMailCompleted = $event;
        break;
      }
      case MAIL_TEMPLATE_TYPE.PASSWORD_CHANGE_EMAIL: {
        this.changePasswordMailCompleted = $event;
        break;

      }
    }

  }
}
